// beforeinstallprompt fires during the very first paint — long before anyone signs in.
// This module is imported from index.js so the browser's install offer is captured immediately
// and can be replayed later (right after sign-in) instead of being lost.
const INSTALLED_KEY = "rq_installed";

let deferred = null;
let installed = false;
const subs = new Set();
const emit = () => subs.forEach((fn) => fn({ deferred, installed }));

export function isInstalled() {
  if (installed) return true;
  try { return localStorage.getItem(INSTALLED_KEY) === "1"; } catch (e) { return false; }
}

function markInstalled() {
  installed = true;
  deferred = null;
  try { localStorage.setItem(INSTALLED_KEY, "1"); } catch (e) { /* storage unavailable */ }
}

export function clearInstalled() {
  installed = false;
  try { localStorage.removeItem(INSTALLED_KEY); } catch (e) { /* storage unavailable */ }
  emit();
}

if (typeof window !== "undefined") {
  installed = isInstalled();
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    // The browser only offers install when the app is NOT installed — so a stale "installed" flag
    // (app was removed from the home screen) is wrong and must be cleared.
    installed = false;
    try { localStorage.removeItem(INSTALLED_KEY); } catch (err) { /* storage unavailable */ }
    emit();
  });
  // Fired whether they installed from our prompt or the browser's 3-dot menu.
  window.addEventListener("appinstalled", () => { markInstalled(); emit(); });
}

export const getInstallState = () => ({ deferred, installed: isInstalled() });

export function subscribeInstall(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

// Returns "accepted" | "dismissed" | "unavailable"
export async function runInstall() {
  if (!deferred) return "unavailable";
  const ev = deferred;
  try {
    ev.prompt();
    const { outcome } = await ev.userChoice;
    if (outcome === "accepted") markInstalled();
    else deferred = null;
    emit();
    return outcome;
  } catch (e) {
    deferred = null;
    emit();
    return "unavailable";
  }
}
